import { defineStore } from 'pinia';
import { nextTick, ref } from 'vue';
import { ElMessage } from 'element-plus';
import {
  analyzeManualFormulaImport,
  chatFormulaImport,
  saveCustomCalculatorFormulasBatch,
  uploadAndAnalyzeFormulaImport,
} from '@/api/calculatorFormulaApi';
import { useAiAssistantStore, type ChatMessage, type MessageRole } from '@/stores/useAiAssistantStore';
import type { CalculatorFormula, FormulaImportChatResult } from '@/types/calculatorFormula';
import { CALCULATOR_FORMULA_IMPORT_SCENARIO } from '@/types/calculatorFormula';
import { importArchiveTitle } from '@/utils/calculatorAiSession';

export type FormulaGenerateStructured = {
  kind: 'calculator_formula_generate';
  status: 'pending' | 'generating' | 'done' | 'cancelled';
  formulaCount: number;
  formulaNames: string[];
};

/** AI 面板宿主：由 AiAssistantPanel 显式绑定，避免 Pinia store 内 inject 失效 */
export type CalculatorFormulaImportPanelHost = {
  getCurrentSessionId: () => string;
  setImportSessionTitle: (title: string) => void;
  archiveAndStartNewSession: (opts?: { archiveTitle?: string }) => Promise<void>;
  persistConversation: (overrideTitle?: string) => Promise<void>;
  expandRightPanel: (force?: boolean) => void;
  setImportBusy: (busy: boolean) => void;
  scrollToBottom: () => void;
};

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

function normalizeFormulas(formulas: CalculatorFormula[], source: 'manual' | 'excel_import') {
  return formulas.map((f, i) => {
    const id = f.id?.startsWith('custom_') ? f.id : `custom_${Date.now()}_${i}_${Math.random().toString(36).slice(2, 6)}`;
    return {
      ...f,
      id,
      isCustom: true,
      icon: f.icon || '📐',
      source,
    };
  });
}

export const useCalculatorFormulaImportStore = defineStore('calculatorFormulaImport', () => {
  const active = ref(false);
  const conversationId = ref('');
  const documentText = ref('');
  const fileName = ref('');
  const importSource = ref<'manual' | 'excel_import'>('excel_import');
  const isProcessing = ref(false);
  const pendingGeneratedFormulas = ref<CalculatorFormula[]>([]);
  const pendingMessageId = ref<string | null>(null);
  const isGeneratingCards = ref(false);
  /** 逐张卡片动画：每次 push 一张待展示公式 */
  const cardRevealQueue = ref<CalculatorFormula[]>([]);
  const appearingFormulaIds = ref<string[]>([]);
  /** 公式卡片生成完成后，延迟自动进入详情页 */
  const pendingAutoEnterFormula = ref<CalculatorFormula | null>(null);
  /** 与 AI 面板当前会话对齐，避免写到场景级键而面板读会话级键 */
  const panelSessionId = ref('');
  const panelHost = ref<CalculatorFormulaImportPanelHost | null>(null);

  const aiStore = useAiAssistantStore();

  function bindPanelHost(host: CalculatorFormulaImportPanelHost | null) {
    panelHost.value = host;
  }

  function resolvePanel(): CalculatorFormulaImportPanelHost | null {
    return panelHost.value;
  }

  function syncPanelSessionId() {
    const sid = String(resolvePanel()?.getCurrentSessionId?.() || '').trim();
    if (sid) panelSessionId.value = sid;
    return panelSessionId.value;
  }

  function importMessages(): ChatMessage[] {
    const sid = panelSessionId.value || syncPanelSessionId();
    if (sid) return aiStore.getMessages(CALCULATOR_FORMULA_IMPORT_SCENARIO, sid);
    return aiStore.getMessages(CALCULATOR_FORMULA_IMPORT_SCENARIO);
  }

  function addImportMessage(
    role: MessageRole,
    content: string,
    structuredResult?: Record<string, unknown> | null,
  ): ChatMessage {
    const sid = panelSessionId.value || syncPanelSessionId();
    if (sid) {
      return aiStore.addMessage(
        CALCULATOR_FORMULA_IMPORT_SCENARIO,
        sid,
        role,
        content,
        undefined,
        undefined,
        structuredResult ?? null,
      );
    }
    return aiStore.addMessage(
      CALCULATOR_FORMULA_IMPORT_SCENARIO,
      role,
      content,
      undefined,
      undefined,
      structuredResult ?? null,
    );
  }

  function updateImportMessage(messageId: string, patch: Partial<ChatMessage>) {
    const sid = panelSessionId.value || syncPanelSessionId();
    if (sid) {
      aiStore.updateMessage(CALCULATOR_FORMULA_IMPORT_SCENARIO, messageId, patch, sid);
      return;
    }
    aiStore.updateMessage(CALCULATOR_FORMULA_IMPORT_SCENARIO, messageId, patch);
  }

  function notifyPanelScroll() {
    void nextTick().then(() => {
      resolvePanel()?.scrollToBottom?.();
    });
  }

  async function beginImportSession(archiveTitle: string) {
    const panel = resolvePanel();
    if (!panel) {
      ElMessage.warning('AI 对话框未就绪，请稍后重试');
      throw new Error('AI panel not ready');
    }

    panel.expandRightPanel(true);
    panel.setImportSessionTitle(archiveTitle);
    panel.setImportBusy(true);

    const prevScenario = aiStore.currentScenario;
    const prevSid = panel.getCurrentSessionId?.() || '';
    const prevMsgs = prevSid
      ? aiStore.getMessages(prevScenario, prevSid)
      : aiStore.getMessages(prevScenario);
    if (prevMsgs.length && prevScenario !== CALCULATOR_FORMULA_IMPORT_SCENARIO) {
      await panel.persistConversation();
    }

    // 先切场景再开新会话，保证 archiveAndStartNewSession 读写 calculator_formula_import
    aiStore.currentScenario = CALCULATOR_FORMULA_IMPORT_SCENARIO;
    await panel.archiveAndStartNewSession({ archiveTitle });
    await nextTick();
    syncPanelSessionId();
    if (!panelSessionId.value) {
      ElMessage.error('无法创建公式对话会话，请刷新页面后重试');
      throw new Error('Failed to sync formula import session');
    }
    active.value = true;
    panel.scrollToBottom();
  }

  function reset() {
    active.value = false;
    conversationId.value = '';
    documentText.value = '';
    fileName.value = '';
    isProcessing.value = false;
    pendingGeneratedFormulas.value = [];
    pendingMessageId.value = null;
    panelSessionId.value = '';
    resolvePanel()?.setImportBusy?.(false);
  }

  function buildGenerateStructured(formulas: CalculatorFormula[], status: FormulaGenerateStructured['status']): FormulaGenerateStructured {
    return {
      kind: 'calculator_formula_generate',
      status,
      formulaCount: formulas.length,
      formulaNames: formulas.map((f) => f.name || '自定义公式'),
    };
  }

  function handleGenerateReady(result: FormulaImportChatResult) {
    const raw = result.generatedFormulas || [];
    if (!raw.length) return null;
    const normalized = normalizeFormulas(raw, importSource.value);
    pendingGeneratedFormulas.value = normalized;
    const assistantMsg = addImportMessage(
      'assistant',
      result.assistantReply || '公式方案已就绪，请点击「开始生成」。',
      buildGenerateStructured(normalized, 'pending') as unknown as Record<string, unknown>,
    );
    pendingMessageId.value = assistantMsg.id;
    notifyPanelScroll();
    return assistantMsg;
  }

  function handleAnalyzeResult(result: FormulaImportChatResult) {
    if (result.conversationId) {
      conversationId.value = result.conversationId;
    }
    if (result.documentText) {
      documentText.value = result.documentText;
    }
    if (result.fileName) {
      fileName.value = result.fileName;
    }
    active.value = true;
    aiStore.currentScenario = CALCULATOR_FORMULA_IMPORT_SCENARIO;
    syncPanelSessionId();

    if (result.phase === 'generate' && result.generatedFormulas?.length) {
      handleGenerateReady(result);
      return;
    }
    addImportMessage('assistant', result.assistantReply || '分析完成。');
    notifyPanelScroll();
    void resolvePanel()?.persistConversation?.(
      importArchiveTitle(importSource.value, fileName.value || '公式'),
    );
  }

  async function startAnalyzeFromFile(file: File): Promise<void> {
    const convId = `calc_import_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    importSource.value = 'excel_import';
    fileName.value = file.name;
    isProcessing.value = true;
    pendingGeneratedFormulas.value = [];
    pendingMessageId.value = null;
    pendingAutoEnterFormula.value = null;

    try {
      await beginImportSession(importArchiveTitle('excel_import', file.name));
      conversationId.value = convId;
      addImportMessage('user', `已上传 Excel 文件「${file.name}」，请分析其中的计算公式。`);
      notifyPanelScroll();

      const result = await uploadAndAnalyzeFormulaImport(file, convId);
      handleAnalyzeResult(result);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '分析失败';
      if (msg !== 'AI panel not ready' && msg !== 'Failed to sync formula import session') {
        ElMessage.error(msg);
        if (panelSessionId.value) {
          addImportMessage('assistant', `分析失败：${msg}`);
          notifyPanelScroll();
        }
      }
    } finally {
      isProcessing.value = false;
      resolvePanel()?.setImportBusy?.(false);
    }
  }

  async function startManualRequest(payload: {
    name: string;
    remark?: string;
    description?: string;
  }): Promise<void> {
    const convId = `calc_manual_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    importSource.value = 'manual';
    documentText.value = [
      `公式名称：${payload.name}`,
      payload.remark?.trim() ? `公式备注：${payload.remark.trim()}` : '',
      payload.description?.trim() ? `公式描述：${payload.description.trim()}` : '',
    ].filter(Boolean).join('\n');
    fileName.value = '';
    isProcessing.value = true;
    pendingGeneratedFormulas.value = [];
    pendingMessageId.value = null;
    pendingAutoEnterFormula.value = null;

    try {
      await beginImportSession(importArchiveTitle('manual', payload.name));
      conversationId.value = convId;

      const userLine = [
        `我想创建公式「${payload.name}」`,
        payload.remark?.trim() ? `备注：${payload.remark.trim()}` : '',
        payload.description?.trim() ? `描述：${payload.description.trim()}` : '',
      ].filter(Boolean).join('\n');
      addImportMessage('user', userLine);
      notifyPanelScroll();

      const result = await analyzeManualFormulaImport({
        formulaName: payload.name,
        remark: payload.remark,
        description: payload.description,
        conversationId: convId,
      });
      handleAnalyzeResult(result);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '推荐失败';
      if (msg === 'AI panel not ready' || msg === 'Failed to sync formula import session') {
        throw e;
      }
      ElMessage.error(msg);
      if (panelSessionId.value) {
        addImportMessage('assistant', `推荐失败：${msg}`);
        notifyPanelScroll();
      }
    } finally {
      isProcessing.value = false;
      resolvePanel()?.setImportBusy?.(false);
    }
  }

  async function sendUserMessage(text: string): Promise<void> {
    const trimmed = text.trim();
    if (!trimmed) return;
    if (!active.value || !conversationId.value) {
      ElMessage.warning('当前没有进行中的公式创建对话，请先导入 Excel 或添加公式');
      return;
    }
    isProcessing.value = true;
    resolvePanel()?.setImportBusy?.(true);
    syncPanelSessionId();
    addImportMessage('user', trimmed);
    notifyPanelScroll();

    const history = importMessages()
      .slice(0, -1)
      .map((m) => ({ role: m.role, content: m.content }));

    try {
      const result = await chatFormulaImport({
        conversationId: conversationId.value,
        message: trimmed,
        documentText: documentText.value,
        fileName: fileName.value,
        history,
      });

      if (result.phase === 'generate' && result.generatedFormulas?.length) {
        handleGenerateReady(result);
      } else {
        addImportMessage('assistant', result.assistantReply || '');
        notifyPanelScroll();
      }
      void resolvePanel()?.persistConversation?.(
        importArchiveTitle(importSource.value, fileName.value || documentText.value.slice(0, 20) || '公式'),
      );
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '对话失败';
      ElMessage.error(msg);
      addImportMessage('assistant', `请求失败：${msg}`);
      notifyPanelScroll();
    } finally {
      isProcessing.value = false;
      resolvePanel()?.setImportBusy?.(false);
    }
  }

  async function confirmGenerate(messageId?: string): Promise<CalculatorFormula[]> {
    const formulas = pendingGeneratedFormulas.value;
    if (!formulas.length || isGeneratingCards.value) return [];

    const msgId = messageId || pendingMessageId.value;
    if (msgId) {
      const msg = importMessages().find((m) => m.id === msgId);
      if (msg?.structuredResult) {
        updateImportMessage(msgId, {
          structuredResult: {
            ...(msg.structuredResult as object),
            status: 'generating',
          },
        });
      }
    }

    isGeneratingCards.value = true;
    appearingFormulaIds.value = [];
    cardRevealQueue.value = [];

    try {
      for (const f of formulas) {
        appearingFormulaIds.value = [...appearingFormulaIds.value, f.id];
        cardRevealQueue.value = [...cardRevealQueue.value, f];
        await sleep(380);
      }

      await saveCustomCalculatorFormulasBatch(
        formulas.map((f) => ({
          formulaId: f.id,
          name: f.name,
          formula: f,
          source: importSource.value,
        })),
      );

      if (msgId) {
        updateImportMessage(msgId, {
          structuredResult: buildGenerateStructured(formulas, 'done') as unknown as Record<string, unknown>,
        });
      }

      ElMessage.success(`已生成 ${formulas.length} 个自定义公式`);
      const created = [...formulas];
      pendingGeneratedFormulas.value = [];
      pendingMessageId.value = null;
      pendingAutoEnterFormula.value = created[0] ?? null;
      // 保留公式创建会话内容，不 reset / 不切换场景
      return created;
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : '生成失败';
      ElMessage.error(msg);
      if (msgId) {
        updateImportMessage(msgId, {
          structuredResult: buildGenerateStructured(formulas, 'pending') as unknown as Record<string, unknown>,
        });
      }
      return [];
    } finally {
      isGeneratingCards.value = false;
    }
  }

  function cancelGenerate(messageId?: string) {
    const msgId = messageId || pendingMessageId.value;
    if (msgId) {
      updateImportMessage(msgId, {
        structuredResult: buildGenerateStructured(pendingGeneratedFormulas.value, 'cancelled') as unknown as Record<string, unknown>,
      });
    }
    pendingGeneratedFormulas.value = [];
    pendingMessageId.value = null;
    reset();
    aiStore.currentScenario = 'calculator_general';
  }

  function clearPendingAutoEnter() {
    pendingAutoEnterFormula.value = null;
  }

  function clearCardRevealQueue() {
    cardRevealQueue.value = [];
  }

  return {
    active,
    conversationId,
    documentText,
    fileName,
    importSource,
    isProcessing,
    pendingGeneratedFormulas,
    pendingMessageId,
    isGeneratingCards,
    cardRevealQueue,
    appearingFormulaIds,
    pendingAutoEnterFormula,
    panelSessionId,
    bindPanelHost,
    reset,
    startAnalyzeFromFile,
    startManualRequest,
    sendUserMessage,
    confirmGenerate,
    cancelGenerate,
    clearCardRevealQueue,
    clearPendingAutoEnter,
  };
});
